import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSitesStore } from '@/stores/sites';
import { Card, Button } from '@/components/UI';
import { AddSiteWizard } from '@/components/Sites/AddSiteWizard';

export function SitesList() {
  const { currentOrganization } = useAuthStore();
  const { sites, fetchSites, isLoading } = useSitesStore();
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  useEffect(() => {
    if (currentOrganization) {
      fetchSites(currentOrganization.id);
    }
  }, [currentOrganization]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="text-gray-600">Manage your tracked websites</p>
        </div>
        <Button onClick={() => setIsWizardOpen(true)}>Add Site</Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <div className="animate-pulse p-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
        ) : sites.length === 0 ? (
          <div className="text-center py-12 px-6">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No sites</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first site.</p>
            <div className="mt-6">
              <Button onClick={() => setIsWizardOpen(true)}>Add your first site</Button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sites.map((site) => (
              <div
                key={site.id}
                className="flex items-center justify-between p-6 hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${site.domain}&sz=64`}
                      alt=""
                      className="w-6 h-6"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                      }}
                    />
                    <svg className="w-5 h-5 text-gray-400 hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{site.name}</h3>
                    <p className="text-sm text-gray-500">{site.domain}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Link to={`/sites/${site.id}`}>
                    <Button variant="secondary" size="sm">
                      View Analytics
                    </Button>
                  </Link>
                  <Link to={`/sites/${site.id}/settings`}>
                    <Button variant="ghost" size="sm">
                      Settings
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add Site Wizard */}
      {currentOrganization && (
        <AddSiteWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          organizationId={currentOrganization.id}
        />
      )}
    </div>
  );
}

export default SitesList;
